import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getUserId } from "components/pages/market/home/MarketHome";

const initialState = {
  monthlyFlowers: [],
  todayFlowers: [],
  searchedMarkets: [],
  autoCompleteWord: [],
  likeList: [],
  todayFlowersIsLoading: false,
  monthlyFlowersIsLoading: false,
};

const fetchMonthlyFlowers = createAsyncThunk(
  "market/fetchMonthlyFlowers",
  async (whole_id) => {
    try {
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/detailview/montly/${whole_id}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const fetchTodayFlowers = createAsyncThunk(
  "market/fetchTodayFlowers",
  async (whole_id) => {
    try {
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/detailview/daily/${whole_id}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const fetchMarkets = createAsyncThunk(
  "market/fetchMarkets",
  async ({ market, sort, page }) => {
    try {
      const userId = getUserId();
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/overview/${userId}/${encodeURIComponent(
          market,
        )}/${encodeURIComponent(sort)}/${page}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

// 시장정보 검색
const marketSearch = createAsyncThunk(
  "market/marketsearch",
  async (payload) => {
    const { user_id, searchNm, page } = payload;

    try {
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/search/${user_id}/${searchNm}/${page}`,
      );

      return data;
    } catch (err) {
      return err.response.data;
    }
  },
);
// 시장정보 검색 자동완성
const marketSearchAutoComplete = createAsyncThunk(
  "market/marketsearchAutoComplete",
  async () => {
    try {
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/search/autoComplete`,
      );
      return data;
    } catch (err) {
      return err.response.data;
    }
  },
);

const fetchLikeList = createAsyncThunk(
  "market/likeList",
  async ({ userId, page }) => {
    try {
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/favorite/get/${userId}/${page}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const likeMarket = createAsyncThunk(
  "market/likeMarket",
  async ({ wholeId }) => {
    try {
      const userId = getUserId();
      const { data } = await axios.post(
        `https://api.floway.co.kr/api/wholesales/favorite/insert/${userId}/${wholeId}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const unlikeMarket = createAsyncThunk(
  "market/unlikeMarket",
  async ({ wholeId }) => {
    try {
      const userId = getUserId();
      const { data } = await axios.delete(
        `https://api.floway.co.kr/api/wholesales/favorite/delete/${userId}/${wholeId}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const fetchMarketDetail = createAsyncThunk(
  "market/fetchMarketDetail",
  async ({ wholeId }) => {
    try {
      const userId = getUserId();
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/detailview/${userId}/${wholeId}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const fetchSimilarMarkets = createAsyncThunk(
  "market/fetchSimilarMarkes",
  async (wholeId) => {
    try {
      const { data } = await axios.get(
        `https://api.floway.co.kr/api/wholesales/detailview/similar/${wholeId}`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  },
);

const marketSlice = createSlice({
  name: "market",
  initialState,
  reducers: {
    resetMarkets(state) {
      state.markets = null;
    },
    resetMarketDetail(state) {
      state.marketDetail = null;
    },
    resetSimilarMarkets(state) {
      state.similarMarkets = null;
    },
    resetMarketLikeList(state) {
      state.likeList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonthlyFlowers.pending, (state) => {
        state.monthlyFlowersIsLoading = false;
      })
      .addCase(fetchMonthlyFlowers.fulfilled, (state, action) => {
        state.monthlyFlowers = action.payload;
        state.monthlyFlowersIsLoading = true;
      })
      .addCase(fetchMonthlyFlowers.rejected, (state) => {
        state.monthlyFlowersIsLoading = true;
      });

    builder
      .addCase(fetchTodayFlowers.pending, (state) => {
        state.todayFlowersIsLoading = false;
      })
      .addCase(fetchTodayFlowers.fulfilled, (state, action) => {
        state.todayFlowers = action.payload;
        state.todayFlowersIsLoading = true;
      })
      .addCase(fetchTodayFlowers.rejected, (state) => {
        state.todayFlowersIsLoading = true;
      });

    builder
      .addCase(fetchMarkets.pending, () => { })
      .addCase(fetchMarkets.fulfilled, (state, action) => {
        state.markets = !action.payload.success ? [] : action.payload.data;
        state.totalMarketsCount = !action.payload.success
          ? 0
          : action.payload.tot_cnt;
      })
      .addCase(fetchMarkets.rejected, () => { });

    builder
      .addCase(fetchLikeList.pending, () => { })
      .addCase(fetchLikeList.fulfilled, (state, action) => {
        state.likeList = action.payload.data;
      })
      .addCase(fetchLikeList.rejected, () => { });

    builder
      .addCase(likeMarket.pending, () => { })
      .addCase(likeMarket.fulfilled, (state, action) => {
        state.likeMarketRes = action.payload;
      })
      .addCase(likeMarket.rejected, () => { });

    builder
      .addCase(unlikeMarket.pending, () => { })
      .addCase(unlikeMarket.fulfilled, (state, action) => {
        state.likeMarketRes = action.payload;
      })
      .addCase(unlikeMarket.rejected, () => { });

    // marketSearch
    builder
      .addCase(marketSearch.pending, () => { })
      .addCase(marketSearch.fulfilled, (state, action) => {
        state.searchedMarkets = action.payload;
      })
      .addCase(marketSearch.rejected, () => { });

    // marketSearchAutoComplete
    builder
      .addCase(marketSearchAutoComplete.pending, () => { })
      .addCase(marketSearchAutoComplete.fulfilled, (state, action) => {
        state.autoCompleteWord = action.payload.data;
      })
      .addCase(marketSearchAutoComplete.rejected, () => { });

    builder
      .addCase(fetchMarketDetail.pending, () => { })
      .addCase(fetchMarketDetail.fulfilled, (state, action) => {
        state.marketDetail = action.payload?.success
          ? action.payload.data
          : null;
      })
      .addCase(fetchMarketDetail.rejected, () => { });

    builder
      .addCase(fetchSimilarMarkets.pending, () => { })
      .addCase(fetchSimilarMarkets.fulfilled, (state, action) => {
        state.similarMarkets = action.payload?.success
          ? action.payload.data
          : [];
      })
      .addCase(fetchSimilarMarkets.rejected, () => { });
  },
});

const MarketCreators = {
  fetchMonthlyFlowers,
  fetchTodayFlowers,
  fetchMarkets,
  likeMarket,
  unlikeMarket,
  marketSearch,
  marketSearchAutoComplete,
  fetchLikeList,
  fetchMarketDetail,
  fetchSimilarMarkets,
};

export { MarketCreators };
const { reducer, actions } = marketSlice;
export const { resetMarkets, resetMarketDetail, resetMarketLikeList } = actions;

export default reducer;
